import { isScrolledToBottom } from "@prismicio/editor-support/DOM"
import { composeRefs } from "@prismicio/editor-support/React"
import { clsx } from "clsx"
import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type UIEvent,
  useRef,
  useState,
} from "react"

import type { ThemeKeys } from "../../theme"
import styles from "./ScrollArea.module.css"
import { ScrollAreaControls } from "./ScrollAreaControls"

export interface ScrollAreaProps {
  "aria-label"?: string
  children?: ReactNode
  className?: string
  direction?: "horizontal" | "vertical"
  // This should be removed in favor of SX, should be resolved by PBD-1327
  style?: CSSProperties
  onScrollToBottom?: () => void
  /**
   * Adds a set of controls to the scroll area.
   * @default false
   */
  controls?: boolean
  /**
   * The threshold for triggering the onScrollToBottom function.
   * @default 50
   */
  threshold?: number
  scrollPaddingBlock?: ThemeKeys<"space">
  stableScrollbar?: boolean
}

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(props, ref) {
    const {
      ["aria-label"]: ariaLabel,
      children,
      direction = "vertical",
      onScrollToBottom,
      controls = false,
      stableScrollbar = true,
      threshold,
      className,
      style,
      scrollPaddingBlock,
    } = props
    const [isBottom, setIsBottom] = useState(false)
    const scrollAreaRef = useRef<HTMLDivElement>(null)

    const { width: scrollbarWidth, height: scrollbarHeight } =
      useScrollbarSize()

    function onScroll(event: UIEvent<HTMLDivElement>) {
      if (!onScrollToBottom) return

      const hasReachedBottom = isScrolledToBottom(
        event.currentTarget,
        threshold,
      )

      if (isBottom !== hasReachedBottom) {
        hasReachedBottom && onScrollToBottom()
        return setIsBottom(hasReachedBottom)
      }
    }

    const scrollAreaComponent = (
      <div
        aria-label={ariaLabel}
        ref={composeRefs(ref, scrollAreaRef)}
        onScroll={onScroll}
        className={clsx(
          styles.root,
          styles[direction],
          stableScrollbar && styles["stable-scrollbar"],
          // if scrollbar is hidden (standard behaviour on macOS)
          // add artificial spacing to avoid layout shifts
          (scrollbarWidth === 0 || scrollbarHeight === 0) && styles.spacing,
          className,
        )}
        style={{
          scrollPaddingBlock,
        }}
      >
        <div className={clsx(styles.content, styles[direction])} style={style}>
          {children}
        </div>
      </div>
    )

    if (controls) {
      return (
        <ScrollAreaControls scrollAreaRef={scrollAreaRef} direction={direction}>
          {scrollAreaComponent}
        </ScrollAreaControls>
      )
    }

    return scrollAreaComponent
  },
)

function useScrollbarSize() {
  const didCompute = useRef(false)
  const widthRef = useRef(0)
  const heightRef = useRef(0)

  if (didCompute.current)
    return {
      width: widthRef.current,
      height: heightRef.current,
    }

  // Create invisible container
  const parent = document.createElement("div")
  parent.classList.add(styles.root) // add root styles to ensure calculations are made on custom scrollbar
  parent.style.visibility = "hidden"
  parent.style.overflow = "scroll" // forcing scrollbar to appear
  document.body.appendChild(parent)

  // Creating inner element and placing it in the container
  const child = document.createElement("div")
  parent.appendChild(child)

  // Calculating difference between the containers full size and the child size
  const scrollbarWidth = parent.offsetWidth - child.offsetWidth
  const scrollbarHeight = parent.offsetHeight - child.offsetHeight

  // Removing temporary elements from the DOM
  parent.parentNode?.removeChild(parent)

  didCompute.current = true
  widthRef.current = scrollbarWidth
  heightRef.current = scrollbarHeight

  return { width: scrollbarWidth, height: scrollbarHeight }
}
